/**
 * Core engine type definitions for Crystals of Polkadot
 * Defines scenes, states, and engine interfaces
 */

// ============================================================================
// Scene Types
// ============================================================================

export enum GameScene {
  TITLE = 'TITLE',
  CHARACTER_CREATION = 'CHARACTER_CREATION',
  NAME_ENTRY = 'NAME_ENTRY',
  INTRO = 'INTRO',
  WORLD = 'WORLD',
  TOWN = 'TOWN',
  DUNGEON = 'DUNGEON',
  BATTLE = 'BATTLE',
  LEVEL_UP = 'LEVEL_UP',
  MENU = 'MENU',
  SAVE_MENU = 'SAVE_MENU',
  DIALOG = 'DIALOG',
  INN = 'INN',
  SHOP = 'SHOP',
  GAME_OVER = 'GAME_OVER',
}

export interface Scene {
  init(): void;
  update(deltaTime: number): void;
  render(ctx: CanvasRenderingContext2D): void;
  handleInput(input: GameInput): void;
  cleanup?(): void;
}

// ============================================================================
// Input System
// ============================================================================

export enum GameKey {
  UP = 'UP',
  DOWN = 'DOWN',
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  A = 'A', // Confirm
  B = 'B', // Cancel/Menu
  START = 'START',
  SELECT = 'SELECT',
}

export interface GameInput {
  pressed: Set<GameKey>;
  justPressed: Set<GameKey>;
  justReleased: Set<GameKey>;
}

// ============================================================================
// Sprite System
// ============================================================================

export interface Sprite {
  id: string;
  image: HTMLImageElement;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AnimatedSprite {
  frames: Sprite[];
  frameDuration: number;
  loop: boolean;
  currentFrame: number;
  elapsed: number;
}

export interface SpriteSheet {
  image: HTMLImageElement;
  tileWidth: number;
  tileHeight: number;
  columns: number;
  rows: number;
}

// ============================================================================
// Map System
// ============================================================================

export interface TileSet {
  image: HTMLImageElement;
  tileWidth: number;
  tileHeight: number;
  firstGid: number;
}

export interface MapLayer {
  name: string;
  data: number[];
  width: number;
  height: number;
  visible: boolean;
  opacity: number;
}

export interface TileMap {
  width: number;
  height: number;
  tileWidth: number;
  tileHeight: number;
  layers: MapLayer[];
  tileSets: TileSet[];
}

export interface Camera {
  x: number;
  y: number;
  width: number;
  height: number;
  target?: { x: number; y: number };
  following?: boolean;
}

// ============================================================================
// Battle System
// ============================================================================

export enum BattlePhase {
  INTRO = 'INTRO',
  MENU = 'MENU',
  TARGETING = 'TARGETING',
  ANIMATING = 'ANIMATING',
  RESULT = 'RESULT',
  VICTORY = 'VICTORY',
  DEFEAT = 'DEFEAT',
}

export interface BattleState {
  phase: BattlePhase;
  currentTurn: number;
  partyHP: number[];
  enemyHP: number[];
  enemies: BattleEnemy[];
  turnQueue: number[];
  animations: BattleAnimation[];
}

export interface BattleEnemy {
  id: number;
  name: string;
  hp: number;
  maxHP: number;
  attack: number;       // Enemy attack power
  defense: number;      // Enemy defense
  magicDefense: number; // Magic defense
  agility: number;      // Turn order / evasion
  x: number;
  y: number;
  spriteId: string;
  dead: boolean;
}

export interface BattleAnimation {
  type: 'ATTACK' | 'SPELL' | 'DAMAGE' | 'HEAL' | 'DEATH';
  target: number;
  value?: number;
  elapsed: number;
  duration: number;
  onComplete?: () => void;
}

// ============================================================================
// Menu System
// ============================================================================

export interface MenuItem {
  label: string;
  enabled?: boolean;
  disabled?: boolean;
  value?: any;
  description?: string;
  submenu?: MenuItem[];
}

export interface MenuWindow {
  x: number;
  y: number;
  width: number;
  height: number;
  padding: number;
  items: MenuItem[];
  selectedIndex: number;
  scrollOffset: number;
  maxVisibleItems: number;
  title?: string;
}

// ============================================================================
// Audio System
// ============================================================================

export interface AudioTrack {
  url: string;
  volume: number;
  loop: boolean;
  buffer?: AudioBuffer;
}

export interface SoundEffect {
  url: string;
  volume: number;
  buffer?: AudioBuffer;
}

// ============================================================================
// Animation System
// ============================================================================

export interface Tween {
  id: string;
  object: any;
  property: string;
  from: number;
  to: number;
  duration: number;
  elapsed: number;
  easing: (t: number) => number;
  onUpdate?: (value: number) => void;
  onComplete?: () => void;
}

export interface AnimationSequence {
  id: string;
  animations: Tween[];
  currentIndex: number;
  loop: boolean;
}

// ============================================================================
// Engine Configuration
// ============================================================================

export interface EngineConfig {
  canvas: HTMLCanvasElement;
  baseWidth: number;
  baseHeight: number;
  scale: number;
  targetFPS: number;
  pixelPerfect: boolean;
}

// ============================================================================
// Engine State
// ============================================================================

export interface EngineState {
  running: boolean;
  paused: boolean;
  currentScene: GameScene;
  fps: number;
  deltaTime: number;
  frameCount: number;
}

// ============================================================================
// Rendering Utilities
// ============================================================================

export interface TextStyle {
  font: string;
  fontSize: number;
  color: string;
  align?: CanvasTextAlign;
  baseline?: CanvasTextBaseline;
  shadow?: boolean;
  shadowColor?: string;
  shadowOffsetX?: number;
  shadowOffsetY?: number;
}

export interface WindowStyle {
  borderColor: string;
  borderWidth: number;
  backgroundColor: string;
  cornerStyle: 'square' | 'rounded' | 'ff1';
  padding: number;
}
